import React, { useState } from 'react';
import { FormControl, InputLabel, Select as MuiSelect } from '@mui/material';
import { useTheme } from 'styled-components';
import { SelectProps } from './select.type';
import { SelectValue } from './value/select-value';
import { IconArrowDown } from '../../icons/arrow-down';
import { cn } from '../../../../utils/tailwind';
import { createStableClassToken } from '../../../../utils/stable-class-token';

export const Select: React.FC<SelectProps> = ({ translationKey, ...props }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const iconClassToken = createStableClassToken('select-icon', [props.size, open]);

    const { renderValue, MenuProps } = props;
    const menuPaperSx = MenuProps?.PaperProps?.sx;
    const mergedMenuPaperSx = [
        {
            marginTop: '0.8rem',
            padding: '0.8rem',
            border: `0.1rem solid ${theme.palette.common.divider}`,
            borderRadius: theme.radius.medium,
            background: theme.palette.background.header,
            boxShadow: theme.shadow.large,
            '& .MuiList-root': {
                padding: 0,
            },
            '& .MuiMenuItem-root': {
                minHeight: '4.4rem',
                padding: '0.8rem 1.2rem',
                borderRadius: theme.radius.medium,
                fontFamily: theme.base.fontFace,
                fontSize: theme.text.size.medium.fontSize,
                lineHeight: theme.text.size.medium.lineHeight,
                fontWeight: theme.base.fontWeight,
                color: theme.palette.common.text,
            },
            '& .MuiMenuItem-root + .MuiMenuItem-root': {
                marginTop: '0.2rem',
            },
            '& .MuiMenuItem-root.Mui-selected': {
                background: `${theme.palette.gradient.primary.main} !important`,
                color: `${theme.palette.color.white.main} !important`,
                fontWeight: 700,
            },
            '& .MuiMenuItem-root.Mui-selected:hover': {
                background: `${theme.palette.gradient.primary.main} !important`,
            },
            '& .MuiMenuItem-root:hover': {
                background: theme.palette.color.secondary.opacity.medium,
            },
        },
        ...(Array.isArray(menuPaperSx) ? menuPaperSx : [menuPaperSx]),
    ].filter(Boolean);

    return (
        <div
            className={cn(
                'relative inline-flex text-left [&_.MuiFormControl-root]:w-full',
                props.fullWidth && 'w-full',
            )}
            style={{ minWidth: `${props.minWidth || 17}rem` }}
        >
            <style>{`
                .${iconClassToken} {
                    background: ${open ? theme.select.hover.open.background : theme.select.base.open.background};
                }

                .${iconClassToken} i {
                    color: ${open ? theme.select.hover.open.color : theme.palette.common.textMuted};
                }

                .${iconClassToken}:hover {
                    background: ${theme.select.hover.open.background};
                }

                .${iconClassToken}:hover i {
                    color: ${theme.select.hover.open.color};
                }
            `}</style>
            <FormControl
                fullWidth={props.fullWidth}
                disabled={props.disabled}
                required={props.required}
                error={props.error}
                className="w-full [&_.MuiFormLabel-root]:left-[1.5rem] [&_.MuiFormLabel-root]:top-[1.8rem] [&_.MuiFormLabel-root]:px-[1rem] [&_.MuiFormLabel-root]:text-medium [&_.MuiFormLabel-root]:transition-all [&_.MuiFormLabel-root]:pointer-events-none [&_.MuiFormLabel-root.MuiFormLabel-filled]:top-[0.6rem] [&_.MuiFormLabel-root.MuiFormLabel-filled]:text-vsmall [&_.MuiFormLabel-root:empty+_.MuiInputBase-root_.MuiSelect-select]:px-[2rem] [&_.MuiSelect-select]:!min-h-0 [&_.MuiSelect-select]:pb-[0.8rem] [&_.MuiSelect-select]:pl-[2.5rem] [&_.MuiSelect-select]:pr-[2.5rem] [&_.MuiSelect-select]:pt-[2.4rem]"
                sx={{
                    '& .MuiFormLabel-root': {
                        color: `${theme.palette.common.textMuted} !important`,
                        fontFamily: theme.base.fontFace,
                        fontWeight: theme.base.fontWeight,
                        lineHeight: theme.text.size.medium.lineHeight,
                        letterSpacing: 0,
                        transform: 'unset',
                    },
                    '& .MuiFormLabel-root.MuiFormLabel-filled': {
                        lineHeight: theme.text.size.vsmall.lineHeight,
                    },
                }}
            >
                <InputLabel>{props.label}</InputLabel>
                <MuiSelect
                    {...props}
                    defaultValue={props.value}
                    onChange={props.onChange}
                    open={open}
                    multiple={props.multiple}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    MenuProps={{
                        ...MenuProps,
                        PaperProps: {
                            ...MenuProps?.PaperProps,
                            sx: mergedMenuPaperSx,
                        },
                    }}
                    IconComponent={() => (
                        <div
                            className={cn(
                                iconClassToken,
                                'mr-[0.7rem] flex h-[4rem] w-[4rem] min-w-[4rem] cursor-pointer items-center justify-center rounded-[99rem] transition-colors [&_i]:h-[1.4rem] [&_i]:w-[1.4rem]',
                                props.size === 'small' &&
                                    'h-[2.6rem] w-[2.6rem] min-w-[2.6rem] [&_i]:h-[1rem] [&_i]:w-[1rem]',
                            )}
                            onClick={() => {
                                setOpen(true);
                            }}
                        >
                            <span
                                className="flex items-center justify-center transition-transform"
                                style={{ transform: open ? 'rotate(180deg)' : 'none' }}
                            >
                                <IconArrowDown />
                            </span>
                        </div>
                    )}
                    renderValue={(option: unknown) => {
                        if (renderValue) return renderValue(option);

                        return (
                            <SelectValue
                                option={option as string | number | Array<string | number> | null}
                                translationKey={translationKey}
                                label={props.label}
                            />
                        );
                    }}
                    className={cn(
                        'w-full [&_.MuiInputBase-input]:text-medium [&_.MuiInputBase-input]:text-text [&_.MuiOutlinedInput-input]:pb-[0.8rem] [&_.MuiOutlinedInput-input]:pl-[2.5rem] [&_.MuiOutlinedInput-input]:pt-[2.4rem] [&_fieldset]:top-0 [&_fieldset]:rounded-[99rem] [&_fieldset]:border [&_fieldset]:border-border hover:[&_fieldset]:border-neutral-gray',
                        props.size === 'small' && 'min-h-[2.4rem]',
                    )}
                    sx={{
                        minHeight: '5.6rem',
                        background: theme.palette.background.body,
                        borderRadius: '99rem !important',
                        '& fieldset': {
                            borderColor: theme.palette.common.border,
                            fontFamily: theme.base.fontFace,
                            fontWeight: theme.base.fontWeight,
                            lineHeight: theme.text.size.medium.lineHeight,
                            outline: 'unset',
                            '& legend': {
                                display: 'none',
                            },
                        },
                        '& .MuiInputBase-input': {
                            fontFamily: theme.base.fontFace,
                            fontSize: theme.text.size.medium.fontSize,
                            lineHeight: theme.text.size.medium.lineHeight,
                            color: `${theme.palette.common.text} !important`,
                        },
                        '&.Mui-focused fieldset': {
                            border: `0.1rem solid ${theme.palette.color.gray.main} !important`,
                        },
                    }}
                >
                    {props.children}
                </MuiSelect>
            </FormControl>
        </div>
    );
};
